import { memo } from 'react';
import PropTypes from 'prop-types';
import { noop, isNil, isFunction } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Hooks
import useHasPermission from 'hooks/UseHasPermission';
//Import Constants
import { SegmentTypesLabels, DefinitionTypes, SegmentObjectType, l, ActionsIcons } from 'constants/common';
// Import UI components
import { Button, Tooltip } from '@geneui/components';
import AccessControl from 'components/common/AccessControl';
import ActionLink from 'components/common/ActionLink';
// Import Enums
import { PermissionEnum, SubscriptionSectionEnum } from 'enums';

const SegmentActionsButtons = ({
    data,
    getConvertSegmentToReportPath,
    unArchiveSegmentAction,
    archiveSegmentAction,
    deleteSegmentAction,
    getCloneSegmentPath,
    editSegmentAction,
    getEditSegmentPath,
    infoSegmentAction,
    isInfoVisible,
    isViewMode,
    getClonePredefinedPath,
}) => {
    const { IsUsed, Type, DefinitionType, TargetObject, ArchivedDate } = data;

    const { t } = useTranslation();
    const { hasSubscriptionSection } = useHasPermission();

    const isPredefinedAvailable =
        hasSubscriptionSection(SubscriptionSectionEnum.Predefined) &&
        Type === SegmentTypesLabels.Dynamic &&
        isFunction(getClonePredefinedPath);

    const isPredefined = DefinitionType === DefinitionTypes.predefined;
    const isArchive = !isNil(ArchivedDate);
    const isStatic = Type === SegmentTypesLabels.Static;

    const isVisibleDelete = () => !isViewMode && !IsUsed && !isPredefined;
    const isVisibleClone = () => !isViewMode && !isStatic;
    const isVisibleEdit = () => !isViewMode && !isArchive && !isPredefined && !isStatic;
    const isVisibleArchive = () => !isViewMode && !isArchive && !isPredefined;
    const isVisibleUnArchive = () => !isViewMode && isArchive;
    const isVisibleConvertToReport = () => !isViewMode && TargetObject !== SegmentObjectType.NonCustomer;

    return (
        <>
            {isVisibleDelete() && (
                <AccessControl permissions={PermissionEnum.DeleteSegment}>
                    <Tooltip text={t(l.DeleteSegment)}>
                        <Button
                            icon={ActionsIcons.Delete}
                            appearance="minimal"
                            color="primary"
                            onClick={() => deleteSegmentAction(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleEdit() && (
                <AccessControl permissions={PermissionEnum.EditSegment}>
                    <Tooltip text={t(l.EditSegment)}>
                        <ActionLink
                            icon={ActionsIcons.Edit}
                            appearance="minimal"
                            color="primary"
                            onClick={() => editSegmentAction(data)}
                            withConfirm={IsUsed}
                            redirectPath={getEditSegmentPath(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleArchive() && (
                <Tooltip text={t(l.ArchiveSegment)}>
                    <Button
                        icon={ActionsIcons.Archive}
                        appearance="minimal"
                        color="primary"
                        onClick={() => archiveSegmentAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleUnArchive() && (
                <Tooltip text={t(l.UnarchiveSegment)}>
                    <Button
                        icon={ActionsIcons.UnArchive}
                        appearance="minimal"
                        color="primary"
                        onClick={() => unArchiveSegmentAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleConvertToReport() && (
                <AccessControl permissions={PermissionEnum.CreateAddHocReport}>
                    <Tooltip text={t(l.CreateReport)}>
                        <ActionLink
                            icon={ActionsIcons.ConvertToReport}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getConvertSegmentToReportPath(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleClone() && (
                <AccessControl permissions={PermissionEnum.CreateSegment}>
                    <Tooltip text={t(l.CloneSegment)}>
                        <ActionLink
                            icon={ActionsIcons.Clone}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getCloneSegmentPath(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}

            {isVisibleClone() && isPredefinedAvailable && (
                <AccessControl permissions={[PermissionEnum.CRMAdmin, PermissionEnum.CRMManager]}>
                    <Tooltip text={t(l.CloneAsPredefined)}>
                        <ActionLink
                            icon={ActionsIcons.Predefined}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getClonePredefinedPath(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}

            {isInfoVisible && (
                <Tooltip text={t(l.SegmentInfo)}>
                    <Button
                        icon={ActionsIcons.Stop}
                        appearance="minimal"
                        color="primary"
                        onClick={() => infoSegmentAction(data)}
                    />
                </Tooltip>
            )}
        </>
    );
};

SegmentActionsButtons.propTypes = {
    data: PropTypes.object.isRequired,
    getConvertToReportPath: PropTypes.func,
    unArchiveSegmentAction: PropTypes.func,
    archiveSegmentAction: PropTypes.func,
    deleteSegmentAction: PropTypes.func,
    getCloneSegmentPath: PropTypes.func,
    editSegmentAction: PropTypes.func,
    getEditSegmentPath: PropTypes.func,
    infoSegmentAction: PropTypes.func,
    getClonePredefinedPath: PropTypes.func,
    isInfoVisible: PropTypes.bool,
    isViewMode: PropTypes.bool,
};

SegmentActionsButtons.defaultProps = {
    getConvertToReportPath: noop,
    unArchiveSegmentAction: noop,
    archiveSegmentAction: noop,
    deleteSegmentAction: noop,
    getCloneSegmentPath: noop,
    editSegmentAction: noop,
    getEditSegmentPath: noop,
    infoSegmentAction: noop,
    isInfoVisible: false,
    isViewMode: false,
};

export default memo(SegmentActionsButtons);
